import { decamelize } from 'humps';
import axios from './axios';
import { PaginatedResponse } from './types';
import {
  Company,
  CompanyAdminResult,
  CompanyUser,
  CompanyApplications,
  CompanyUserInvitation,
  ThirdPartyConnections,
  UserRole,
  CompanyAdminFilters,
  InvoiceFinancingCompanyAdminFilters,
  InvoiceFinancingCompanyAdminResult,
} from '../types/companyUser';
import { APIAsset } from '../types/application';
import { FactoringRequest } from '../types/factoring';
import { getCountryKey, getInternalPipelineStatus, transformFactoringRequest } from '../store';

function createFilterParams(filters: Record<string, any>): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        value.forEach((item) => params.append(decamelize(key), item));
      }
    } else if (value !== null && value !== undefined && value !== '') {
      params.append(decamelize(key), value.toString());
    }
  });
  return params;
}

export function getCompanies(filters: CompanyAdminFilters) {
  const params = createFilterParams(filters);
  return axios
    .get<PaginatedResponse<CompanyAdminResult>>(`/admins/companies/list/?${params.toString()}`)
    .then(({ data }) => data);
}

export function getInvoiceFinancingCompanies(filters: InvoiceFinancingCompanyAdminFilters) {
  const params = createFilterParams(filters);
  return axios
    .get<PaginatedResponse<InvoiceFinancingCompanyAdminResult>>(
      `/admins/companies/list_invoice_financing/?${params.toString()}`
    )
    .then(({ data }) => data);
}

type ExtendedCompany = Company & { factoringRequest: FactoringRequest; status: string };

export async function getCompanyApplicationsDetail(id: string): Promise<CompanyApplications> {
  const extendedCompany = await axios
    .get<ExtendedCompany>(`/admins/companies/${id}/`)
    .then(({ data }) => data);
  const { factoringRequest, status, ...company } = extendedCompany;
  company.country = getCountryKey(company.country);
  company.internalPipelineStatus = company.productStatus.readyForFactoring
    ? 'closed'
    : getInternalPipelineStatus(factoringRequest);
  return {
    company: company as Company,
    applications: factoringRequest ? [transformFactoringRequest(factoringRequest, company as Company)] : [],
    applicationCompletion: factoringRequest?.completionPercent,
    signupLink: company.signupLink,
    status,
  };
}

export function getCompanyUserActiveFactoringRequest(companyId: number) {
  return axios
    .get<FactoringRequest | ''>(`/companies/${companyId}/products/factoring/request/`)
    .then(({ data }) => data || null);
}

export function currentUserDetail(): Promise<CompanyUser> {
  return axios.get<CompanyUser>(`/company_users/me/`).then(({ data }) => data);
}

export function getAssets(): Promise<APIAsset[]> {
  return axios.get(`/company_users/me/assets/`).then(({ data }) => data || []);
}

export function syncFSCompanyUser(companyUuid: string): Promise<CompanyUser> {
  return axios.post(`/admins/companies/lms_write/${companyUuid}/`).then(({ data }) => data);
}

export function thirdPartyConnections(companyId: number, applicationId: number) {
  return axios
    .get<ThirdPartyConnections>(
      `/companies/${companyId}/products/factoring/request/${applicationId}/third_party_services/`
    )
    .then(({ data }) => data);
}

export function connectToThirdPartyServices(companyId: number, factoringRequestId: number) {
  return axios
    .post(`/companies/${companyId}/products/factoring/request/${factoringRequestId}/third_party_services/`, {
      satWs: true,
    })
    .then(({ data }) => data);
}

export async function updateCompanyUser(payload: {
  user: { firstName: string; lastName: string };
  contactPhone: string;
}) {
  const companyUser = await axios.patch<CompanyUser>(`/company_users/me/`, payload).then(({ data }) => data);
  return companyUser;
}

export async function createCompany({
  hubspotCompanyId,
  referralId,
}: {
  hubspotCompanyId: string | null;
  referralId: string | null;
}) {
  const company = await axios
    .post<Company>(`/companies/`, {
      ...(hubspotCompanyId ? { hubspotCompanyId } : {}),
      ...(referralId ? { referralId } : {}),
    })
    .then(({ data }) => data);
  return company;
}

export function updateCompany(companyId: number, payload: any) {
  return axios.patch<Company>(`/companies/${companyId}/`, payload).then(({ data }) => data);
}

export function discardDraftCompany(companyId: number) {
  return axios.delete(`/companies/${companyId}/`);
}

export function completeOnboarding(companyId: number) {
  return axios.post<Company>(`/companies/${companyId}/complete/`).then(({ data }) => data);
}

export function updateUserPreferences(payload: { language: string }) {
  return axios.patch(`/users/me/preferences/`, payload);
}

export function getCompanyUserInvitations(companyId: number) {
  return axios
    .get<CompanyUserInvitation[]>(`/companies/${companyId}/user_invitations/`)
    .then(({ data }) => data);
}

export function inviteUser(
  companyId: number,
  payload: { userFirstName: string; userLastName: string; userEmail: string; role: UserRole }
) {
  return axios
    .post<CompanyUserInvitation>(`/companies/${companyId}/user_invitations/`, payload)
    .then(({ data }) => data);
}

export function changePassword(payload: { oldPassword: string; newPassword: string }): Promise<any> {
  return axios.patch('/password/change-password/', payload);
}

export function resendInvitation(companyId: number, invitationId: number): Promise<any> {
  return axios
    .post(`/companies/${companyId}/user_invitations/${invitationId}/resend/`)
    .then(({ data }) => data);
}

export function deleteInvitation(companyId: number, invitationId: number): Promise<any> {
  return axios.delete(`/companies/${companyId}/user_invitations/${invitationId}/`).then(({ data }) => data);
}

export function editMember(
  companyId: number,
  userRoleId: number,
  payload: { role?: UserRole; jobTitle?: string }
): Promise<any> {
  return axios
    .patch(`/companies/${companyId}/company_user_roles/${userRoleId}/`, payload)
    .then(({ data }) => data);
}

export function deleteMember(companyId: number, userRoleId: number): Promise<any> {
  return axios.delete(`/companies/${companyId}/company_user_roles/${userRoleId}/`).then(({ data }) => data);
}

export function confirmAcknowledgementAgreement(companyId: number): Promise<any> {
  return axios.post(`/companies/${companyId}/set_agreement_signature_date/`).then(({ data }) => data);
}
